// A form generated from a catalogue command's JSON schema (G15). Forms are never hand-coded per command: the engine's
// pydantic schema says which fields exist, which are required, their types and defaults. The browser never supplies an
// actor, an agent or a stamp (FORBIDDEN_FIELDS), and an engagement command's `engagement` is implied by the request
// (IMPLIED_FIELDS). `fixed` fields come from the engine's action (e.g. the id being decided) and are shown read-only.
// Free-text fields start empty: the words are the person's own, never canned. A list of objects ($ref items) is entered as
// JSON, checked before sending.
import { useId, useState, type FormEvent } from "react";
import type { CommandSpec, JsonSchema, ToFill } from "../data/types";
import { FORBIDDEN_FIELDS, IMPLIED_FIELDS, cleanPayload, deref, effective, validatePayload, withToFill } from "../lib/commands";
import { Button, Field, inputCls } from "./primitives";

const WORDS = /^(text|reason|response|note|body|guidance|statement|conclusion|basis|resolution|rationale|result|what|why)$/;

export function SchemaForm({ spec: given, fixed = {}, initial = {}, onSubmit, submitLabel, onCancel, personName, busy, hideFields = [], toFill }: {
  spec: CommandSpec; fixed?: Record<string, unknown>; initial?: Record<string, unknown>; onSubmit: (payload: Record<string, unknown>) => void | Promise<void>;
  submitLabel?: string; onCancel?: () => void; personName?: string; busy?: boolean; hideFields?: string[];
  /** What the engine's action says the person must still fill (`payload.to_fill`): hints, required marks, validated before sending. */
  toFill?: ToFill[];
}) {
  const uid = useId();
  const spec = withToFill(given, toFill);
  const fill = new Set((toFill ?? []).map((f) => f.field));
  const root = spec.inputSchema;
  const props = Object.entries(root.properties ?? {}).filter(([k]) => !FORBIDDEN_FIELDS.has(k) && !IMPLIED_FIELDS.has(k) && !hideFields.includes(k));
  const required = new Set(spec.inputSchema.required ?? []);
  // Schema defaults prefill choices (enums, numbers, yes/no) — never free text, which stays the person's own words.
  const [values, setValues] = useState<Record<string, unknown>>(() => {
    const d: Record<string, unknown> = {};
    for (const [k, raw] of props) { const v = raw.default ?? effective(raw).schema.default; if (v !== undefined && v !== null && v !== "" && typeof v !== "string") d[k] = v; else if (typeof v === "string" && effective(raw).schema.enum) d[k] = v; }
    return { ...d, ...initial };
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const set = (k: string, v: unknown) => setValues((s) => ({ ...s, [k]: v }));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const parsed: Record<string, unknown> = { ...values, ...fixed };
    const errs: Record<string, string> = {};
    for (const [k, v] of Object.entries(parsed)) {           // JSON-entered lists of objects
      if (typeof v === "string" && isObjectList(root.properties?.[k], root)) {
        try { parsed[k] = v.trim() ? JSON.parse(v) : undefined; } catch { errs[k] = "Not valid JSON: a list like [{\"id\": 1, \"value\": \"…\"}]."; }
      }
    }
    const payload = cleanPayload(spec, parsed);
    for (const [k, v] of Object.entries(validatePayload(spec, payload))) errs[k] ??= v;
    setErrors(errs);
    if (Object.keys(errs).length) return;
    await onSubmit(payload);
  };

  return (
    <form onSubmit={submit} noValidate className="space-y-3" aria-label={spec.summary}>
      {spec.humanOnly && (
        <p className="rounded-md border border-waiting-line bg-waiting-bg px-3 py-2 text-xs text-waiting">
          This is your own act{personName ? `, recorded as ${personName}` : ""}. The engine stamps the date when it runs it; no agent may do this for you.
        </p>
      )}
      {Object.entries(fixed).filter(([k]) => spec.inputSchema.properties?.[k]).map(([k, v]) => (
        <div key={k} className="flex items-baseline gap-2 text-sm">
          <span className="text-muted">{spec.inputSchema.properties?.[k]?.title ?? k}:</span>
          <span className="font-mono">{Array.isArray(v) ? v.join(", ") : String(v)}</span>
        </div>
      ))}
      {fill.size > 0 && <p className="text-xs text-muted">The engine needs from you: {[...fill].filter((f) => !(f in fixed)).map((f) => spec.inputSchema.properties?.[f]?.title ?? f).join(", ")}.</p>}
      {props.filter(([k]) => !(k in fixed)).sort(([a], [b]) => Number(fill.has(b)) - Number(fill.has(a))).map(([k, raw]) => {
        const { schema: eff, nullable } = effective(raw);
        const schema = eff.type === "array" && eff.items ? { ...eff, items: deref(eff.items, root) } : deref(eff, root);
        const id = `${uid}-${k}`;
        const req = required.has(k) && !nullable && raw.default === undefined;
        const label = `${schema.title ?? k}${req ? "" : " (optional)"}`;
        return (
          <Field key={k} id={id} label={label} hint={schema.description} error={errors[k]} required={req}>
            <Input id={id} name={k} schema={schema} value={values[k]} onChange={(v) => set(k, v)} invalid={!!errors[k]} required={req} />
          </Field>
        );
      })}
      {errors._ && <p role="alert" className="text-xs text-blocked">{errors._}</p>}
      <div className="flex justify-end gap-2 pt-1">
        {onCancel && <Button onClick={onCancel}>Cancel</Button>}
        <Button kind="primary" type="submit" disabled={busy}>{submitLabel ?? "Send to the engine"}</Button>
      </div>
    </form>
  );
}

function Input({ id, name, schema, value, onChange, invalid, required }: { id: string; name: string; schema: JsonSchema; value: unknown; onChange: (v: unknown) => void; invalid: boolean; required: boolean }) {
  const common = { id, name, "aria-invalid": invalid || undefined, "aria-describedby": [schema.description ? `${id}-hint` : "", invalid ? `${id}-err` : ""].filter(Boolean).join(" ") || undefined, "aria-required": required || undefined };
  if (schema.enum) {
    return (
      <select {...common} className={inputCls} value={value === undefined || value === null ? "" : String(value)}
        onChange={(e) => onChange(e.target.value === "" ? undefined : schema.type === "integer" || schema.type === "number" ? Number(e.target.value) : e.target.value)}>
        <option value="">Choose…</option>
        {schema.enum.map((o) => <option key={String(o)} value={String(o)}>{String(o).replace(/_/g, " ")}</option>)}
      </select>
    );
  }
  if (schema.type === "boolean") {
    return <input {...common} type="checkbox" className="h-4 w-4" checked={value === true} onChange={(e) => onChange(e.target.checked)} />;
  }
  if (schema.type === "integer" || schema.type === "number") {
    return <input {...common} type="number" inputMode="decimal" step={schema.type === "integer" ? 1 : "any"} className={inputCls} value={value === undefined || value === null ? "" : String(value)}
      onChange={(e) => onChange(e.target.value === "" ? undefined : Number(e.target.value))} />;
  }
  if (schema.type === "array" && schema.items?.type === "object") {
    const keys = Object.keys(schema.items.properties ?? {});
    return <textarea {...common} className={`${inputCls} min-h-[80px] font-mono text-xs`} placeholder={`[{${keys.map((x) => `"${x}": …`).join(", ")}}]`}
      value={typeof value === "string" ? value : value ? JSON.stringify(value) : ""} onChange={(e) => onChange(e.target.value)} />;
  }
  if (schema.type === "array") {
    const itemNum = schema.items?.type === "integer" || schema.items?.type === "number";
    return <input {...common} className={inputCls} placeholder="Separate with commas" value={Array.isArray(value) ? value.join(", ") : ""}
      onChange={(e) => onChange(e.target.value.split(",").map((x) => x.trim()).filter(Boolean).map((x) => (itemNum ? Number(x) : x)))} />;
  }
  if (schema["x-widget"] === "textarea" || (schema["x-widget"] !== "text" && WORDS.test(name))) {
    return <textarea {...common} className={`${inputCls} min-h-[80px]`} value={(value as string) ?? ""} onChange={(e) => onChange(e.target.value)} />;
  }
  return <input {...common} type={schema.format === "date" ? "date" : "text"} className={inputCls} value={(value as string) ?? ""} onChange={(e) => onChange(e.target.value)} />;
}

/** A property that is a list of objects (pydantic `$ref` items), entered as JSON. */
function isObjectList(raw: JsonSchema | undefined, root: JsonSchema): boolean {
  if (!raw) return false;
  const s = effective(raw).schema;
  return s.type === "array" && !!s.items && deref(s.items, root).type === "object";
}
